import { useEffect, useCallback, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SortingState } from '@tanstack/react-table';
import { Button } from '@/components/common/Button';
import { KPICard } from '@/components/data-display/KPICard';
import { useConveraLogs } from './useConveraLogs';
import { DirectionFilter } from './DirectionFilter';
import { LogTable } from './LogTable';
import { LogDetailModal } from './LogDetailModal';
import type { ConveraApiLogDto, DirectionFilter as DirectionFilterValue } from './types';

/** Read URL param helpers — stable references for mount-only effect. */
function readUrlDirection(params: URLSearchParams): DirectionFilterValue | null {
  const v = params.get('direction');
  return v === 'outbound' || v === 'inbound' ? v : null;
}
function readUrlInt(params: URLSearchParams, key: string): number | null {
  const v = params.get(key);
  if (!v) return null;
  const n = parseInt(v, 10);
  return n > 0 ? n : null;
}

const AUTO_REFRESH_INTERVAL_MS = 30_000;

export function ConveraLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLog, setSelectedLog] = useState<ConveraApiLogDto | null>(null);

  const store = useConveraLogs();
  const {
    logs,
    isLoading,
    error,
    total,
    outboundCount,
    inboundCount,
    errorCount,
    direction,
    page,
    limit,
    sortBy,
    sortDirection,
    autoRefresh,
    fetchLogs,
    setDirection,
    setPage,
    setLimit,
    setSortBy,
    setAutoRefresh,
    clearError,
  } = store;

  // ---------------------------------------------------------------------------
  // URL <-> store <-> fetch sync
  //
  // Two effects only:
  //   1. Mount-only: hydrate the store from URL params. Does NOT fetch — the
  //      second effect picks up the state change on the next render and fires
  //      the initial fetch.
  //   2. State -> URL + fetch: a single source of truth. Writes the current
  //      store state back to the URL and fetches.
  //
  // The previous three-effect chain ([searchParams], [state -> URL],
  // [state -> fetch]) ping-ponged via setSearchParams -> searchParams update
  // -> hydrate -> fetch, firing fetchLogs 2-4 times per user action. With
  // 100-row pages × 8 cells under React 19 StrictMode the renderer fell
  // behind, isLoading oscillated, and the page hung.
  //
  // Trade-off: browser back/forward navigation no longer re-hydrates the
  // store from the URL. Acceptable for v1; restoring it requires a popstate
  // listener that does not collapse back into the cycle. Follow-up ticket.
  // ---------------------------------------------------------------------------

  const initialised = useRef(false);

  // react-router-dom v7's `setSearchParams` is wrapped in `useCallback` with
  // `[navigate, searchParams]` as deps — so its identity changes every time
  // we call it. Keeping it directly in the second effect's deps would loop
  // (effect calls setSearchParams -> searchParams updates -> setSearchParams
  // identity changes -> effect re-runs -> calls fetchLogs again). Stash it in
  // a ref so the effect's identity check stays gated on the store fields we
  // actually care about.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // Mount-only hydrate. The empty dep array is intentional — `searchParams`
  // and the Zustand setters are only consulted once on mount; subsequent
  // reads of the store happen in the store->URL+fetch effect below.
  // (eslint-plugin-react-hooks is not configured for this workspace, so no
  // rule disable is required.)
  useEffect(() => {
    const dir = readUrlDirection(searchParams);
    const pg = readUrlInt(searchParams, 'page');
    const lim = readUrlInt(searchParams, 'limit');

    // Order matters: setDirection and setLimit both reset page to 1 (see
    // useConveraLogs.ts), so setPage must run last for the URL's `?page=N`
    // value to stick.
    if (dir) setDirection(dir);
    if (lim) setLimit(lim);
    if (pg) setPage(pg);

    initialised.current = true;
  }, []);

  useEffect(() => {
    if (!initialised.current) return;

    const params: Record<string, string> = {};
    if (direction !== 'all') params.direction = direction;
    if (page !== 1) params.page = String(page);
    if (limit !== 50) params.limit = String(limit);
    setSearchParamsRef.current(params, { replace: true });

    fetchLogs();
  }, [direction, page, limit, sortBy, sortDirection, fetchLogs]);

  // ---------------------------------------------------------------------------
  // Auto-refresh
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => {
      fetchLogs();
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [autoRefresh, fetchLogs]);

  // ---------------------------------------------------------------------------
  // Sorting bridge — TanStack SortingState <-> store
  // ---------------------------------------------------------------------------

  const sorting: SortingState = sortBy ? [{ id: sortBy, desc: sortDirection === 'desc' }] : [];

  const handleSortingChange = useCallback(
    (newSorting: SortingState) => {
      if (newSorting.length === 0) {
        setSortBy('created_at', 'desc');
      } else {
        setSortBy(newSorting[0].id, newSorting[0].desc ? 'desc' : 'asc');
      }
    },
    [setSortBy],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div data-testid="convera-logs-page">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-[22px] font-extrabold text-[#1a1a1a]">Convera Exchange Logs</h2>
          <p className="mt-0.5 text-[13px] text-[#999]">
            All API calls and webhook notifications with Convera
          </p>
        </div>
        <div className="flex items-center gap-3">
          <DirectionFilter
            value={direction}
            onChange={(val: DirectionFilterValue) => {
              setDirection(val);
            }}
          />
          <label className="flex items-center gap-1.5 text-[12px] text-[#666]">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-[#ddd] text-[#6B0F1A] focus:ring-[#6B0F1A]"
              data-testid="auto-refresh-toggle"
            />
            Auto-refresh
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fetchLogs()}
            data-testid="refresh-button"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats cards — server-aggregated. Cards reflect the same filtered
          set the table shows: a `?direction=outbound` view sees Total =
          Outbound and Inbound = 0; date filters narrow all four. */}
      <div className="mb-6 grid grid-cols-4 gap-4" data-testid="logs-stats">
        <KPICard value={total.toLocaleString()} label="Total Exchanges" />
        <KPICard value={outboundCount.toLocaleString()} label="Outbound" />
        <KPICard value={inboundCount.toLocaleString()} label="Inbound" />
        <KPICard value={errorCount.toLocaleString()} label="Errors" />
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="mb-4 flex items-center justify-between rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-[13px] text-[#c62828]"
          data-testid="logs-error-banner"
        >
          <span>{error}</span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                clearError();
                fetchLogs();
              }}
              data-testid="retry-button"
            >
              Retry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearError}
              data-testid="dismiss-error-button"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <LogTable
        logs={logs}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        onRowClick={setSelectedLog}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
      />

      {/* Detail modal */}
      <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}
